/**
 * Safe HTML rendering helper — sanitizes untrusted HTML strings via DOMPurify
 * before injecting them via `dangerouslySetInnerHTML`.
 *
 * ★ 2026-08-10 — Introdotto durante il code-quality pass. Utilizzato per:
 *   - Preview email delle proposte (Libreria + Ordini)
 *   - Ogni futuro rendering di HTML editabile dagli admin.
 *
 * Regole di sanificazione:
 *   - Rimuove <script>, event handler inline (`onclick`, ecc.), <iframe> non
 *     whitelisted (a parte quelli usati nel Master Template preview via `srcDoc`).
 *   - Consente attributi safe come style, class, href, src, alt, target/rel.
 */
import DOMPurify from 'dompurify';
import { logger } from './logger';

const DEFAULT_CONFIG = {
  // Consenti tag HTML "email safe" (compresi h1..h6, tabelle, immagini)
  ALLOWED_TAGS: [
    'a', 'b', 'blockquote', 'br', 'code', 'div', 'em', 'h1', 'h2', 'h3', 'h4',
    'h5', 'h6', 'hr', 'i', 'img', 'li', 'ol', 'p', 'pre', 'span', 'strong',
    'sub', 'sup', 'table', 'tbody', 'td', 'th', 'thead', 'tr', 'u', 'ul',
    'font', 'small', 'section', 'article', 'header', 'footer', 'figure',
    'figcaption', 'address', 'nav',
  ],
  ALLOWED_ATTR: [
    'href', 'target', 'rel', 'src', 'alt', 'title', 'class', 'style', 'width',
    'height', 'align', 'valign', 'colspan', 'rowspan', 'bgcolor', 'color',
    'border', 'cellpadding', 'cellspacing',
  ],
  // Non permettere data: URIs (a parte immagini base64 opzionali)
  ALLOW_DATA_ATTR: false,
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|data:image\/):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
};

/**
 * Sanitize an HTML string before rendering.
 *
 * @param {string} raw — untrusted HTML
 * @param {object} [config] — override the default DOMPurify options
 * @returns {string} sanitized HTML (safe to pass to dangerouslySetInnerHTML)
 */
export function sanitizeHtml(raw, config = DEFAULT_CONFIG) {
  if (raw === null || raw === undefined) return '';
  try {
    return DOMPurify.sanitize(String(raw), config);
  } catch (err) {
    // Fallback drastico se DOMPurify fallisce → togliamo qualunque tag.
    logger.warn('[sanitizeHtml] DOMPurify failed:', err);
    return String(raw).replace(/<[^>]*>/g, '');
  }
}

/**
 * Wrapper JSX praticità: renderizza HTML sanitizzato in un contenitore.
 * Usage:
 *   <SafeHtml html={htmlBody} className="prose" />
 */
export function SafeHtml({ html, className, tag = 'div', ...rest }) {
  const Tag = tag;
  return (
    <Tag
      className={className}
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
      {...rest}
    />
  );
}

export default sanitizeHtml;
